import React, { useEffect, useMemo, useState } from 'react';
import { Card, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { MetricConfig } from '../../types/metrics';
import { apiService } from '../../services/apiClient';
import LineChart from './charts/LineChart';
import { MetricTitle } from '../ui/MetricTitle';
import { getChartSubtitle } from '../../utils/chartSubtitles';
import './CydCohortRateChart.css';

type Granularity = 'week' | 'month';

interface CohortRatePoint {
  date: string;
  value: number;
  incomplete?: boolean;
}

interface CydCohortRateChartProps {
  config: MetricConfig;
  storeId: string | null;
  startDate?: string;
  endDate?: string;
}

function autoGranularity(startDate: string, endDate: string): Granularity {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const days = Math.ceil((end.getTime() - start.getTime()) / 86_400_000) + 1;
  return days > 60 ? 'month' : 'week';
}

const CydCohortRateChart: React.FC<CydCohortRateChartProps> = ({
  config,
  storeId,
  startDate,
  endDate,
}) => {
  const [granularity, setGranularity] = useState<Granularity>(() =>
    startDate && endDate ? autoGranularity(startDate, endDate) : 'week'
  );

  useEffect(() => {
    if (startDate && endDate) {
      setGranularity(autoGranularity(startDate, endDate));
    }
  }, [startDate, endDate]);

  const { data, isLoading, error } = useQuery({
    queryKey: [config.id, storeId, startDate, endDate, granularity],
    queryFn: async () => {
      if (!storeId || !startDate || !endDate || !config.apiEndpoint) {
        throw new Error('Missing parameters');
      }

      let endpoint = config.apiEndpoint.replace('{storeId}', storeId);
      const url = new URL(endpoint, process.env.REACT_APP_API_URL || window.location.origin);
      url.searchParams.set('startDate', startDate);
      url.searchParams.set('endDate', endDate);
      url.searchParams.set('granularity', granularity);
      endpoint = url.pathname + url.search;

      const response = await apiService.get<CohortRatePoint[]>(endpoint);
      return response.data ?? [];
    },
    enabled: !!storeId && !!startDate && !!endDate && !!config.apiEndpoint,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const chartData = useMemo(() => data ?? [], [data]);
  const subtitle = getChartSubtitle(config.id, { granularity });

  if (error) {
    return (
      <Card className="metric-panel" shadow="sm" padding="md" radius="md" withBorder>
        <MetricTitle title={config.title} metricId={config.id} subtitle={subtitle} mb="md" />
        <Text c="negative">Error: {(error as Error).message}</Text>
      </Card>
    );
  }

  return (
    <Card className="metric-panel" shadow="sm" padding="md" radius="md" withBorder>
      <div className="cyd-cohort-chart__header">
        <MetricTitle title={config.title} metricId={config.id} subtitle={subtitle} mb={0} />
        <div className="cyd-cohort-chart__controls">
          <button
            type="button"
            className={`cyd-cohort-chart__pill${granularity === 'week' ? ' cyd-cohort-chart__pill--active' : ''}`}
            onClick={() => setGranularity('week')}
          >
            Semana
          </button>
          <button
            type="button"
            className={`cyd-cohort-chart__pill${granularity === 'month' ? ' cyd-cohort-chart__pill--active' : ''}`}
            onClick={() => setGranularity('month')}
          >
            Mes
          </button>
        </div>
      </div>

      {isLoading ? (
        <Text mt="md">Cargando gráfico...</Text>
      ) : chartData.length === 0 ? (
        <Text mt="md">Sin datos disponibles</Text>
      ) : (
        <>
          <LineChart
            data={chartData}
            title={config.title}
            loading={isLoading}
            valueFormat="percent"
            showIncompleteZone
          />
          <Text size="xs" c="dimmed" mt="xs" className="cyd-cohort-chart__footnote">
            La zona sombreada marca cohortes recientes donde los CyD pueden seguir llegando.
          </Text>
        </>
      )}
    </Card>
  );
};

export default CydCohortRateChart;
